import { useState, useMemo } from "react";
import {
    Search,
    XCircle,
    Key,
    Database,
    Cpu,
    Activity,
    Package,
    Users,
    Shield,
    Network,
    AlertTriangle,
    CircuitBoard,
    Bug,
    Server,
    Router,
    Camera,
    ShieldCheck,
    LayoutDashboard,
    Share2,
    Layers,
    Table,
    Link2,
    Radio,
    Zap,
    EthernetPort,
    ChevronLeft,
    ChevronRight,
    ServerCog,
    Thermometer,
    Logs,
    GalleryVerticalEnd,
    Folder,
    AlarmClockCheck,
    BrickWallShield,
    History,
    ShieldEllipsis,
    Clock,
    FileText,
    Terminal,
    Boxes
} from "lucide-react";


export function AssetSidebarTabs({
    activeTab,
    onTabChange,
    info,
}: {
    activeTab: string;
    onTabChange: (tabId: string) => void;
    info?: {
        agent_type?: string;
        hasSnmpData?: boolean;
        asset_type?: string;
        platform?: string;
        has_oob?: boolean;
        oob_type?: string;
    };
}) {
    const [tabSearchTerm, setTabSearchTerm] = useState("");
    const [isOpen, setIsOpen] = useState(false);


    // Platform detection
    const platform = info?.platform?.toLowerCase() || "";
    const isLinux = platform === "linux" || platform === "ubuntu";
    const isDarwin = platform === "darwin" || platform === "macos";
    const isWindows = platform === "windows";


    // Add state to track the hovered tab and its vertical position
    const [hoveredTab, setHoveredTab] = useState<{ label: string; top: number } | null>(null);


    // Group 1: The standard server tabs (excluding OOB/iLO tabs)
    const baseStandardTabs = [
        { id: "overview", label: "Overview", icon: <LayoutDashboard className="w-4 h-4" /> },
        { id: "compliance", label: "Compliance", icon: <Activity className="w-4 h-4" /> },
        { id: "license", label: "License", icon: <Key className="w-4 h-4" /> },
        { id: "raid", label: "RAID", icon: <Database className="w-4 h-4" /> },
        { id: "hardware", label: "Hardware", icon: <Cpu className="w-4 h-4" /> },
        // { id: "memory", label: "Memory", icon: <MemoryStick className="w-4 h-4" /> },
        // { id: "performance", label: "Performance", icon: <Activity className="w-4 h-4" /> },
        { id: "software", label: "Software", icon: <Package className="w-4 h-4" /> },
        { id: "users", label: "Users", icon: <Users className="w-4 h-4" /> },
        { id: "service_monitoring", label: "Service Monitoring", icon: <ServerCog className="w-4 h-4" /> },
        { id: "services", label: "Services", icon: <Server className="w-4 h-4" /> },
        { id: "patches", label: "Windows Patches", icon: <ShieldCheck className="w-4 h-4" /> },
        { id: "security", label: "Security", icon: <Shield className="w-4 h-4" /> },
        { id: "ports", label: "Ports", icon: <Network className="w-4 h-4" /> },
        { id: "certificates", label: "SSL", icon: <ShieldCheck className="w-4 h-4" /> },
        // { id: "interfaces", label: "Network Interfaces", icon: <EthernetPort className="w-4 h-4" /> },
        { id: "issues", label: "Issues", icon: <AlertTriangle className="w-4 h-4" /> },
        { id: "network-firewall", label: "Network Firewall", icon: <Shield className="w-4 h-4" /> },
        { id: "pci-devices", label: "PCI Devices", icon: <CircuitBoard className="w-4 h-4" /> },
        { id: "processes", label: "Processes", icon: <Activity className="w-4 h-4" /> },
        // { id: "vulnerabilities", label: "Vulnerabilities", icon: <Bug className="w-4 h-4" /> },
        { id: "windows-defender", label: "Windows Defender", icon: <ShieldEllipsis className="w-4 h-4" /> },
        { id: "shared-folders", label: "Shared Folders", icon: <Folder className="w-4 h-4" /> },
        { id: "scheduled-tasks", label: "Scheduled Tasks", icon: <AlarmClockCheck className="w-4 h-4" /> },
        { id: "firewall-rules", label: "Firewall Rules", icon: <BrickWallShield className="w-4 h-4" /> },
        { id: "logon-history", label: "Logon History", icon: <History className="w-4 h-4" /> },
        { id: "cron-jobs", label: "Cron Jobs", icon: <Clock className="w-4 h-4" /> },
        { id: "kernel-modules", label: "Kernel Modules", icon: <Cpu className="w-4 h-4" /> },
        { id: "containers", label: "Containers", icon: <Boxes className="w-4 h-4" /> },
        { id: "log-monitor", label: "Log Monitor", icon: <FileText className="w-4 h-4" /> },
        // { id: "db-monitor", label: "DB Monitor", icon: <Database className="w-4 h-4" /> },
    ];


    // Group 2: The specific iLO tabs
    const iloTabs = [
        { id: "assetsilo", label: "Details", icon: <Server className="w-4 h-4" /> },
        { id: "ilo-event-logs", label: "Event Logs", icon: <Logs className="w-4 h-4" /> },
        { id: 'ilo-issues', label: 'Issues', icon: <AlertTriangle className="w-4 h-4" /> },
        { id: 'ilo-temperatures', label: 'Temperatures', icon: <Thermometer className="w-4 h-4" /> },
        { id: 'ilo-firmware', label: 'Firmware', icon: <GalleryVerticalEnd className="w-4 h-4" /> },
        { id: 'ilo-protocols', label: 'Protocols', icon: <Share2 className="w-4 h-4" /> }
    ];


    const probeBaseTabs = [
        { id: "vulnerabilities", label: "Vulnerabilities", icon: <Bug className="w-4 h-4" /> },
        { id: "ports_probe", label: "Ports", icon: <Network className="w-4 h-4" /> },
    ];


    const snmpTabs = [
        { id: "snmp-info", label: "SNMP Info", icon: <Server className="w-4 h-4" /> },
        { id: "snmp-hardware", label: "SNMP Hardware", icon: <Cpu className="w-4 h-4" /> },
        { id: "snmp-interfaces", label: "SNMP Interfaces", icon: <Router className="w-4 h-4" /> },
    ];


    const currentTabs = useMemo(() => {
        const isIlo = info?.oob_type?.toLowerCase() === "ilo";


        // Pre-calculate OS specific standard tabs
        const platformStandardTabs = baseStandardTabs.filter(tab => {
            // 1. Hide License and Windows Patches on Linux & Darwin

            if ((isLinux || isDarwin) && (tab.id === "license" || tab.id === "patches" || tab.id === "windows-defender" || tab.id === "shared-folders" || tab.id === "scheduled-tasks" || tab.id === "security")) {

                return false;
            }

            // Hide RAID on Darwin
            if (isDarwin && tab.id === "raid"){
                return false;
            }

            // 2. Hide Service Monitoring on Darwin, Windows, or any non-Linux
            if (!isLinux && (tab.id === "cron-jobs" || tab.id === "kernel-modules")) {
                return false;
            }
            return true;
        });


        // 1. Check for OOB / iLO specifically first, since it overrides standard server logic
        if (info?.has_oob && isIlo) {
            // If it's a local_asset, we want platform-specific standard tabs + iLO tabs
            if (info?.asset_type === "local_asset") {
                return [...platformStandardTabs, ...iloTabs].filter(tab => tab.id !== 'overview');
            } else {
                // If it's anything else (like "server"), we ONLY want iLO tabs
                return iloTabs;
            }
        }


        // 2. Specific Hardware Types
        if (info?.asset_type === "switch") {
            return [
                { id: "discovered-interfaces", label: "Discovered Interfaces", icon: <Network className="w-4 h-4" /> },
                { id: "switch-ssh", label: "SSH", icon: <Terminal className="w-4 h-4" /> },
                { id: "discovered-neighbors", label: "Discovered Neighbors", icon: <Share2 className="w-4 h-4" /> },
                { id: "switch-hardware", label: "Hardware Info", icon: <Cpu className="w-4 h-4" /> },
                { id: "vlans", label: "VLANs", icon: <Layers className="w-4 h-4" /> },
                { id: "arp-entries", label: "ARP Entries", icon: <Activity className="w-4 h-4" /> },
                { id: "mac-table", label: "MAC Table", icon: <Table className="w-4 h-4" /> },
                { id: "tcp-connections", label: "TCP Connections", icon: <Link2 className="w-4 h-4" /> },
                { id: "udp-listeners", label: "UDP Listeners", icon: <Radio className="w-4 h-4" /> },
                { id: "poe", label: "POE Info", icon: <Zap className="w-4 h-4" /> },
                { id: "switch-ports", label: "Ports", icon: <EthernetPort className="w-4 h-4" /> }
            ];
        }


        if (info?.asset_type === "camera") {
            return [
                { id: "camera-overview", label: "Camera Overview", icon: <LayoutDashboard className="w-4 h-4" /> },
                { id: "camera-interface", label: "Camera Interface", icon: <Camera className="w-4 h-4" /> },
                { id: "protocol", label: "Protocol", icon: <ShieldCheck className="w-4 h-4" /> },
                { id: "camera-users", label: "Users", icon: <Users className="w-4 h-4" /> }
            ];
        }

        // ILO standalone asset type
        if (info?.asset_type === "ilo") {
            return iloTabs;
        }


        // 3. Specific Agent Types
        if (info?.agent_type === "probe") {
            let tabs = [...probeBaseTabs];
            if (info.hasSnmpData) {
                tabs = [...tabs, ...snmpTabs];
            }
            return tabs;
        }


        if (info?.agent_type === 'lightweight') {
            return baseStandardTabs.filter(tab => tab.id === 'ciphers');
        }


        // 4. Default Server Logic (has_oob is false, or not iLO)
        return platformStandardTabs;


    }, [info?.agent_type, info?.hasSnmpData, info?.asset_type, info?.platform, info?.has_oob, info?.oob_type, isLinux, isDarwin, isWindows]);


    const filteredTabOptions = useMemo(() => {
        if (!tabSearchTerm) return currentTabs;
        const term = tabSearchTerm.toLowerCase();
        return currentTabs.filter(
            (tab) => tab.label.toLowerCase().includes(term) || tab.id.toLowerCase().includes(term)
        );
    }, [tabSearchTerm, currentTabs]);


    return (
        <>
            <div
                className={`bg-gray-900 border-r border-gray-600 flex flex-col h-full transition-all duration-300 ease-in-out shrink-0 overflow-hidden ${isOpen ? 'w-60' : 'w-14'}`}
            >
                {/* Stable Search/Toggle Container */}
                <div className="h-12 w-full shrink-0 border-b border-gray-600/30 relative">
                    {/* Expanded State: Search Input + Close Button */}
                    <div
                        className={`absolute inset-0 flex items-center px-2 gap-2 transition-all duration-200 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                    >
                        <div className="relative flex-1">
                            <input
                                type="text"
                                placeholder="Search tabs..."
                                value={tabSearchTerm}
                                onChange={(e) => setTabSearchTerm(e.target.value)}
                                className="w-full bg-gray-800 border border-gray-600 rounded-md px-2 py-1 pl-8 text-white placeholder-gray-500 focus:outline-none focus:border-orange-600 text-[11px]"
                            />
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
                            {tabSearchTerm && (
                                <button
                                    onClick={() => setTabSearchTerm('')}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200"
                                >
                                    <XCircle className="w-3.5 h-3.5" />
                                </button>
                            )}
                        </div>


                        <button
                            onClick={() => setIsOpen(false)}
                            className="shrink-0 p-1.5 bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white rounded-md transition-colors"
                            title="Collapse Sidebar"
                        >
                            <ChevronLeft className="w-4 h-4" />
                        </button>
                    </div>


                    {/* Collapsed State: Open Button */}
                    <div
                        className={`absolute inset-0 flex items-center justify-center transition-all duration-200 ${isOpen ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
                    >
                        <button
                            onClick={() => setIsOpen(true)}
                            className="p-1.5 bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white rounded-md transition-colors"
                            title="Expand Sidebar"
                        >
                            <ChevronRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>


                {/* Tab List */}
                <div className="flex-1 overflow-y-auto overflow-x-hidden custom-scroll p-1.5 space-y-1 pb-20">
                    {filteredTabOptions.length > 0 ? (
                        filteredTabOptions.map((tab) => (
                            <button
                                key={tab.id}
                                onMouseEnter={(e) => !isOpen && setHoveredTab({ label: tab.label, top: e.currentTarget.getBoundingClientRect().top })}
                                onMouseLeave={() => setHoveredTab(null)}
                                onClick={() => {
                                    onTabChange(tab.id);
                                    setHoveredTab(null);
                                }}
                                className={`w-full flex items-center h-8 rounded-md transition-all duration-200 relative group ${isOpen ? 'px-3 justify-start' : 'justify-center'} ${activeTab === tab.id
                                    ? 'bg-orange-700 text-white shadow-sm'
                                    : 'text-gray-200 hover:text-white hover:bg-gray-800'
                                    }`}
                            >
                                <span className="shrink-0 flex items-center justify-center w-5 h-5">
                                    {tab.icon}
                                </span>


                                <span
                                    className={`ml-3 font-bold text-[11px] uppercase tracking-wider whitespace-nowrap transition-all duration-300 ${isOpen ? 'opacity-100 visible' : 'opacity-0 invisible absolute w-0 overflow-hidden'}`}
                                >
                                    {tab.label}
                                </span>
                            </button>
                        ))
                    ) : (
                        isOpen && (
                            <div className="text-center py-10">
                                <Search className="w-5 h-5 text-gray-700 mx-auto mb-2" />
                                <p className="text-gray-500 text-[10px] font-bold uppercase">No Matches</p>
                            </div>
                        )
                    )}
                </div>
            </div>


            {/* Floating Tooltip */}
            {!isOpen && hoveredTab && (
                <div
                    className="fixed z-[9999] px-2.5 py-1.5 bg-orange-600 text-white text-[10px] font-bold uppercase tracking-widest rounded-md shadow-xl whitespace-nowrap pointer-events-none transition-opacity duration-200"
                    style={{ left: '4rem', top: hoveredTab.top + 2 }}
                >
                    {hoveredTab.label}
                    <div className="absolute -left-1 top-1/2 -translate-y-1/2 w-2 h-2 bg-orange-600 rotate-45 rounded-sm"></div>
                </div>
            )}
        </>
    );
}